import type { ReactNode } from 'react';
import type {
  AppFooterProps,
  NavLinkProps,
} from '@standardsdigital/ui-components/AppFooter';
import AppFooter from '@standardsdigital/ui-components/AppFooter';
import type { Client, LogoLangCode } from '@standardsdigital/ui-components/Logo';
import Icon from '@standardsdigital/ui-components/Icon';
import ScreenReaderOnly from '@standardsdigital/ui-components/ScreenReaderOnly';
import getTranslation from '@/util/getTranslation';
import getLogoLangCode from '@/util/getLogoLangCode';
import type { FooterModel } from '@/redux/services/generatedApi/footer';
import styles from './Footer.module.css';
import type { NullableOptional } from '@/types/util';

export enum SocialMedia {
  LinkedIn = 'linkedin',
  Facebook = 'facebook',
  YouTube = 'youtube',
  Instagram = 'instagram',
}

export interface FooterProps {
  translations: string | undefined;
  language: string | undefined;
  data: NullableOptional<FooterModel> | null;
  renderLogo: (clientCode: Client, langCode: LogoLangCode | undefined) => ReactNode;
}

function Footer({
  translations: jsonTranslations,
  language,
  data,
  renderLogo,
}: Readonly<FooterProps>) {
  const translations: unknown = JSON.parse(jsonTranslations || '{}');

  const { navigationLinks, organizations = [], socialLinks = [] } = data || {};

  const langCode = getLogoLangCode(language);

  const links: NavLinkProps['links'] =
    navigationLinks?.map((link) => ({
      link: (
        <a href={link?.url ?? undefined} data-auto="footer-navigation-link">
          {link?.name}
          {link?.invisibleTextTranslation ? (
            <ScreenReaderOnly>{link.invisibleTextTranslation}</ScreenReaderOnly>
          ) : null}
          {link?.isExternal ? <Icon name="ArrowUpRight" /> : null}
        </a>
      ),
      isCurrent:
        typeof window !== 'undefined' && link?.url === window.location.pathname,
    })) || [];

  const navLinkProps: NavLinkProps = {
    links,
    'aria-label': getTranslation(
      'Plugins.Misc.SDTheme.Footer.NavigationLinks',
      translations,
    ),
    'data-auto': 'footer-navigation-links',
  };

  const organizationInfo = organizations
    ?.map((organization) => organization?.logo)
    ?.map((logo) => ({
      logo: (
        <a
          href={logo?.url || '/'}
          data-auto="logo-link-footer"
          aria-label={logo?.invisibleTextTranslation || logo?.imageAlt || undefined}
        >
          {renderLogo(logo?.clientCode as Client, langCode)}
        </a>
      ),
    }));

  const socialMediaLinks =
    socialLinks?.reduce(
      (acc, currentValue) => {
        const linkName = currentValue?.name?.toLowerCase();
        const socialMedia = acc;
        if (linkName === SocialMedia.LinkedIn && currentValue?.url) {
          socialMedia.linkedinLink = currentValue.url;
        }
        if (linkName === SocialMedia.Facebook && currentValue?.url) {
          socialMedia.facebookLink = currentValue.url;
        }
        if (linkName === SocialMedia.YouTube && currentValue?.url) {
          socialMedia.youtubeLink = currentValue.url;
        }
        if (linkName === SocialMedia.Instagram && currentValue?.url) {
          socialMedia.instagramLink = currentValue.url;
        }
        return socialMedia;
      },
      {} as Record<keyof NonNullable<AppFooterProps['socialMediaLinks']>, string>,
    ) || {};

  return (
    <AppFooter
      organizationInfo={organizationInfo}
      navLinkProps={navLinkProps}
      className={styles.footer}
      socialMediaLinks={socialMediaLinks}
      scrollToTopText={
        data
          ? getTranslation('Plugins.Misc.SDTheme.Footer.ScrollToTop', translations)
          : ''
      }
      data-auto="footer"
    />
  );
}

export default Footer;
